import CryptoJS from 'crypto-js';
import { RoleInfo } from '../types/auth';

/**
 * Get role information from localStorage for access control
 */
export const getRoleInfo = (): RoleInfo => {
  try {
    const ciphertext = localStorage.getItem('awardRoleInfo');
    if (!ciphertext) {
      return {};
    }
    const secretKey = import.meta.env.VITE_SALT || 'fallback-secret-key';
    const bytes = CryptoJS.AES.decrypt(ciphertext, secretKey);
    const decryptedData = bytes.toString(CryptoJS.enc.Utf8);
    if (!decryptedData) {
      return {};
    }
    return JSON.parse(decryptedData);
  } catch {
    return {};
  }
};

/**
 * Set role information in localStorage with encryption
 */
export const setRoleInfo = (roleInfo: RoleInfo): void => {
  try {
    const secretKey = import.meta.env.VITE_SALT || 'fallback-secret-key';
    const ciphertext = CryptoJS.AES.encrypt(
      JSON.stringify(roleInfo),
      secretKey
    ).toString();
    localStorage.setItem('awardRoleInfo', ciphertext);
  } catch (error) {
    console.error('Failed to encrypt/store role info:', error);
  }
};

const IDP_BASE_URL = import.meta.env.VITE_IDP_BASE_URL;

export const generateAuthHeaders = (): Record<string, string> => {
  const timestamp = Math.floor(Date.now() / 1000); // Current time in seconds
  const secretKey = import.meta.env.VITE_SALT; // Ensure this is set in your environment

  const signature = CryptoJS.HmacSHA256(
    timestamp.toString(),
    secretKey
  ).toString(CryptoJS.enc.Hex);

  return {
    'X-Timestamp': timestamp.toString(),
    'X-Signature': signature,
  };
};

// Function to decode the access token and extract the subject
export const decodeAccessToken = async (
  accessToken: string
): Promise<string> => {
  if (accessToken === 'localhost-dummy-token') {
    return 'localhost-user';
  }

  const response = await fetch(`${IDP_BASE_URL}/api/oauth/validate`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });

  if (!response.ok) {
    const error: any = await response.json();
    error.status = 403;
    throw error; // Throw the raw error object
  }

  const data = await response.json();

  const subject = data.Subject; // Extract the Subject field from the decoded response
  if (!subject) {
    const error = new Error('Subject not found in the response');
    throw error; // Throw the raw error object
  }

  return subject;
};

// Fetch the user profile using the Subject
export const fetchUserProfile = async (accessToken: string): Promise<any> => {
  if (accessToken === 'localhost-dummy-token') {
    return {
      FullName: 'Localhost User',
      DN: 'cn=localhost,ou=users,dc=example,dc=com',
    };
  }
  // Step 1: Validate the token and get the Subject
  const subject = await decodeAccessToken(accessToken);

  // Step 2: Use the Subject to get the user profile
  const response = await fetch(`${IDP_BASE_URL}/api/v1/users/${subject}`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw error; // Throw the raw error object
  }

  const profileData = await response.json();
  return profileData; // Return profile data (FullName, DN, etc.)
};

export const handleLoginCallback = async (code: string): Promise<string> => {
  if (code === 'dummy-code') {
    return 'localhost-dummy-token';
  }

  const response = await fetch(`${IDP_BASE_URL}/api/oauth/refresh`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${code}`,
    },
  });

  if (!response.ok) {
    const error: any = await response.json();
    error.status = 403;
    throw error; // Throw the raw error object
  }

  const data = await response.json();
  return data.access; // Return access token
};

export const generateRandomState = (): string => {
  const existingState = localStorage.getItem('oauth_state');
  if (existingState) {
    return existingState;
  }

  const state =
    Math.random().toString(36).substring(2, 15) +
    Math.random().toString(36).substring(2, 15);
  localStorage.setItem('oauth_state', state);
  return state;
};

export const refreshToken = async (code: string): Promise<string> => {
  if (code === 'dummy-code') {
    return 'localhost-dummy-token';
  }

  const response = await fetch(`${IDP_BASE_URL}/api/oauth/refresh`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${code}`,
    },
  });

  if (!response.ok) {
    const error: any = await response.json();
    error.status = 403;
    throw error; // Throw the raw error object
  }

  const data = await response.json();
  return data.access; // Return access token
};

export const initiateLogin = (): void => {
  // Store the current path to redirect back after login
  localStorage.setItem('redirect_path', window.location.pathname);

  const isLocalhost = window.location.hostname === 'localhost';
  if (isLocalhost) {
    const redirectUri = import.meta.env.VITE_IDP_REDIRECT_URI;
    window.location.href = `${redirectUri}?code=dummy-code`;
    return;
  }

  const state = generateRandomState();
  const clientId = import.meta.env.VITE_IDP_CLIENT_ID;
  const redirectUri = import.meta.env.VITE_IDP_REDIRECT_URI;

  const loginUrl = `${IDP_BASE_URL}/oauth/authorize?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=code&state=${state}`;
  window.location.href = loginUrl;
};

export const logout = (): void => {
  // Clear local storage and redirect to home
  localStorage.removeItem('user');
  localStorage.removeItem('oauth_code');
  localStorage.removeItem('oauth_state');
  window.location.href = '/';
};

export const getXAuthInfoHeader = (): Record<string, string> => {
  const storedUser = localStorage.getItem('user');
  if (!storedUser) {
    return {};
  }

  try {
    const user = JSON.parse(storedUser);
    if (user && user.profile && user.awardRole) {
      const { email } = user.profile;
      const { role, pillar, category } = user.awardRole;
      const authInfo = `${email}|${role}|${pillar || ''}|${category || ''}`;
      return { 'X-Auth-Info': authInfo };
    }
  } catch (error) {
    console.error('Error parsing user from localStorage:', error);
  }

  return {};
};
